
import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Plus, Edit, Trash2 } from 'lucide-react'

import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

const OperatorResources = () => {
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingResource, setEditingResource] = useState(null)
  const [operatorInfo, setOperatorInfo] = useState<any>(null)
  const { toast } = useToast()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    module: '',
    domain: '',
    priority: false,
    file_url: ''
  })

  const domains = [
    { value: 'self', label: 'Self' },
    { value: 'wealth', label: 'Wealth' },
    { value: 'social', label: 'Social' }
  ]

  const modules = [
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'emotional-crisis', label: 'Emotional Crisis Mgmt.' },
    { value: 'personal-growth', label: 'Personal Growth' },
    { value: 'budgeting', label: 'Budgeting' },
    { value: 'investing', label: 'Investing' },
    { value: 'passive-income', label: 'Passive Income' },
    { value: 'communication', label: 'Communication' },
    { value: 'networking', label: 'Networking' },
    { value: 'relationships', label: 'Relationships' }
  ]

  useEffect(() => {
    fetchOperatorInfo()
    fetchResources()
  }, [])

  const fetchOperatorInfo = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      const { data: operator } = await supabase
        .from('operators')
        .select('*')
        .eq('user_id', session.user.id)
        .single()
      
      setOperatorInfo(operator)
    }
  }

  const fetchResources = async () => {
    try {
      const { data, error } = await supabase
        .from('school_resources')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setResources(data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching resources",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!operatorInfo) return

    try {
      const resourceData = {
        ...formData,
        school_id: operatorInfo.school_id,
        operator_id: operatorInfo.id,
        file_url: formData.file_url || null
      }

      if (editingResource) {
        const { error } = await supabase
          .from('school_resources')
          .update(resourceData)
          .eq('id', editingResource.id)
        
        if (error) throw error
        toast({ title: "Resource updated successfully" })
      } else {
        const { error } = await supabase
          .from('school_resources')
          .insert(resourceData)
        
        if (error) throw error
        toast({ title: "Resource created successfully" })
      }

      setDialogOpen(false)
      setEditingResource(null)
      setFormData({
        title: '',
        description: '',
        module: '',
        domain: '',
        priority: false,
        file_url: ''
      })
      fetchResources()
    } catch (error: any) {
      toast({
        title: "Error saving resource",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleEdit = (resource: any) => {
    setEditingResource(resource)
    setFormData({
      title: resource.title,
      description: resource.description || '',
      module: resource.module || '',
      domain: resource.domain || '',
      priority: resource.priority,
      file_url: resource.file_url || ''
    })
    setDialogOpen(true)
  }

  const handleDelete = async (resourceId: string) => {
    try {
      const { error } = await supabase
        .from('school_resources')
        .delete()
        .eq('id', resourceId)

      if (error) throw error
      toast({ title: "Resource deleted successfully" })
      fetchResources()
    } catch (error: any) {
      toast({
        title: "Error deleting resource",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Resources Management</CardTitle>
          <CardDescription>Loading resources...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Resources Management</CardTitle>
            <CardDescription>Create and manage resources for your school students</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingResource(null)
                setFormData({
                  title: '',
                  description: '',
                  module: '',
                  domain: '',
                  priority: false,
                  file_url: ''
                })
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingResource ? 'Edit Resource' : 'Create New Resource'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Select value={formData.domain} onValueChange={(value) => setFormData({ ...formData, domain: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      {domains.map(domain => (
                        <SelectItem key={domain.value} value={domain.value}>
                          {domain.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="module">Module</Label>
                  <Select value={formData.module} onValueChange={(value) => setFormData({ ...formData, module: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select module" />
                    </SelectTrigger>
                    <SelectContent>
                      {modules.map(module => (
                        <SelectItem key={module.value} value={module.value}>
                          {module.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="file_url">File URL (optional)</Label>
                  <Input
                    id="file_url"
                    type="url"
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    placeholder="https://example.com/file.pdf"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="priority"
                    checked={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.checked })}
                  />
                  <Label htmlFor="priority">Priority Resource</Label>
                </div>
                <Button type="submit" className="w-full">
                  {editingResource ? 'Update Resource' : 'Create Resource'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Domain</TableHead>
              <TableHead>Module</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {resources.map((resource: any) => (
              <TableRow key={resource.id}>
                <TableCell className="font-medium">{resource.title}</TableCell>
                <TableCell>
                  {resource.domain && <Badge variant="outline">{resource.domain}</Badge>}
                </TableCell>
                <TableCell>{resource.module}</TableCell>
                <TableCell>
                  {resource.priority && <Badge variant="destructive">Priority</Badge>}
                </TableCell>
                <TableCell>
                  {resource.file_url ? (
                    <a href={resource.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      View File
                    </a>
                  ) : (
                    'No file'
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(resource)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(resource.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {resources.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No resources created yet. Click "Add Resource" to create your first resource.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

export default OperatorResources
